import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../Context/LanguageContext';

const Etkinlikler = (props) => {
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState('upcoming');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);

  // Close lightbox on Escape key
  const closeLightbox = useCallback(() => setLightboxImage(null), []);
  useEffect(() => {
    if (!lightboxImage) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeLightbox();
    };
    document.addEventListener('keydown', handleKeyDown);
    // Lock body scroll when lightbox is open
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [lightboxImage, closeLightbox]);

  const getMonthName = (date) => {
    return date.toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', { month: 'long' });
  };

  // Separate events into upcoming and past based on date
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const allEvents = props.data?.etkinlikler || [];
  
  const upcomingEvents = allEvents
    .filter(event => new Date(event.date) >= today)
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  
  const pastEvents = allEvents
    .filter(event => new Date(event.date) < today)
    .sort((a, b) => new Date(b.date) - new Date(a.date)); // Most recent first

  const renderEventCard = (event, index, isPast = false) => {
    const date = new Date(event.date);
    const day = date.getDate();
    const month = getMonthName(date);
    const year = date.getFullYear();

    if (isPast) {
      return (
        <motion.div 
          key={index} 
          className="past-event-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1, duration: 0.4 }}
          onClick={() => setSelectedEvent(selectedEvent === index ? null : index)}
        >
          <div className="past-event-header">
            <div className="past-event-date">
              <span className="day">{day}</span>
              <span className="month">{month}</span>
              <span className="year">{year}</span>
            </div>
            <div className="past-event-info">
              <h3>{event.name}</h3>
              <p>{event.description}</p>
              {event.link && (
                <a 
                  href={event.link} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="event-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  {t('events.details')} <i className="fa fa-arrow-right" aria-hidden="true" style={{marginLeft: '5px'}}></i>
                </a>
              )}
            </div>
            {event.images && event.images.length > 0 && (
              <div className="gallery-indicator">
                <i className="fa fa-images" aria-hidden="true"></i>
                <span>{event.images.length} {language === 'tr' ? 'fotoğraf' : 'photos'}</span>
              </div>
            )}
          </div>
          
          <AnimatePresence>
            {selectedEvent === index && event.images && event.images.length > 0 && (
              <motion.div 
                className="past-event-gallery"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="gallery-grid">
                  {event.images.map((img, imgIndex) => (
                    <motion.div 
                      key={imgIndex} 
                      className="gallery-item"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: imgIndex * 0.05 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setLightboxImage(img);
                      }}
                    >
                      <img 
                        src={`images/etkinlikler/${img}`} 
                        alt={`${event.name} - ${imgIndex + 1}`}
                        loading="lazy"
                      />
                      <div className="gallery-overlay">
                        <i className="fa fa-expand" aria-hidden="true"></i>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      );
    }

    // Upcoming event card (original style)
    return (
      <motion.div 
        key={index} 
        className="event-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1, duration: 0.4 }}
      >
        <div className="event-date">
          <span className="day">{day}</span>
          <span className="month-year">{month} {year}</span>
        </div>
        <div className="event-details">
          <h3>{event.name}</h3>
          <p>{event.description}</p>
          {event.link && (
            <a href={event.link} target="_blank" rel="noopener noreferrer" className="event-link">
              {t('events.details')} <i className="fa fa-arrow-right" aria-hidden="true" style={{marginLeft: '5px'}}></i>
            </a>
          )}
        </div>
      </motion.div>
    );
  };

  const renderNoEvents = (type) => {
    if (type === 'upcoming') {
      return (
        <div className="no-upcoming-events">
          <div className="no-events-content">
            <div className="no-events-icon">
              <i className="fa fa-calendar-alt" aria-hidden="true"></i>
            </div>
            <h3>{language === 'tr' ? 'Yaklaşan Etkinlik Bulunmamaktadır' : 'No Upcoming Events'}</h3>
            <p>
              {language === 'tr' 
                ? 'Şu anda planlanmış bir etkinlik bulunmamaktadır. Yeni etkinliklerimizden haberdar olmak için sosyal medya hesaplarımızı takip edin!' 
                : 'There are no scheduled events at this time. Follow our social media accounts to stay updated on new events!'}
            </p>
            <div className="no-events-social">
              <a href="https://www.linkedin.com/company/ktgpb/" target="_blank" rel="noopener noreferrer" className="social-link">
                <i className="fa fa-linkedin" aria-hidden="true"></i>
                LinkedIn
              </a>
              <a href="https://www.instagram.com/ktgpb/" target="_blank" rel="noopener noreferrer" className="social-link">
                <i className="fa fa-instagram" aria-hidden="true"></i>
                Instagram
              </a>
            </div>
          </div>
          <div className="no-events-illustration">
            <div className="calendar-illustration">
              <div className="cal-header"></div>
              <div className="cal-body">
                <div className="cal-dot"></div>
                <div className="cal-dot"></div>
                <div className="cal-dot"></div>
                <div className="cal-dot highlight"></div>
                <div className="cal-dot"></div>
                <div className="cal-dot"></div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="no-events">
        <i className="fa fa-calendar-check" aria-hidden="true"></i>
        <h3>{language === 'tr' ? 'Geçmiş etkinlik bulunmamaktadır' : 'No past events'}</h3>
      </div>
    );
  };

  return (
    <section id="etkinlikler">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6 }}
        >
          <h2>{t('events.title')}</h2>
          <p className="section-subtitle">
            {language === 'tr' 
              ? 'Topluluğumuzu bir araya getiren etkinliklerimiz' 
              : 'Events that bring our community together'}
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <div className="events-tabs">
          <button 
            className={`events-tab ${activeTab === 'upcoming' ? 'active' : ''}`}
            onClick={() => setActiveTab('upcoming')}
          >
            <i className="fa fa-calendar" aria-hidden="true"></i>
            {language === 'tr' ? 'Yaklaşan Etkinlikler' : 'Upcoming Events'}
            {upcomingEvents.length > 0 && <span className="tab-badge">{upcomingEvents.length}</span>}
          </button>
          <button 
            className={`events-tab ${activeTab === 'past' ? 'active' : ''}`}
            onClick={() => setActiveTab('past')}
          >
            <i className="fa fa-history" aria-hidden="true"></i>
            {language === 'tr' ? 'Geçmiş Etkinlikler' : 'Past Events'}
            {pastEvents.length > 0 && <span className="tab-badge">{pastEvents.length}</span>}
          </button>
        </div>

        {/* Events Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'upcoming' ? (
            <motion.div 
              key="upcoming"
              className="events-grid"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              {upcomingEvents.length > 0 
                ? upcomingEvents.map((event, index) => renderEventCard(event, index, false))
                : renderNoEvents('upcoming')}
            </motion.div>
          ) : (
            <motion.div 
              key="past"
              className="past-events-list"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {pastEvents.length > 0 
                ? pastEvents.map((event, index) => renderEventCard(event, index, true))
                : renderNoEvents('past')}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxImage && (
          <motion.div 
            className="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightboxImage(null)}
            role="dialog"
            aria-modal="true"
            aria-label={language === 'tr' ? 'Fotoğraf görüntüleyici' : 'Photo viewer'}
          >
            <button
              className="lightbox-close"
              onClick={() => setLightboxImage(null)}
              aria-label={language === 'tr' ? 'Kapat' : 'Close'}
            >
              <i className="fa fa-times" aria-hidden="true"></i>
            </button>
            <motion.img 
              src={`images/etkinlikler/${lightboxImage}`}
              alt="Event photo"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Etkinlikler;